import React, { useState } from 'react';
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Loader2,
  LockKeyhole,
  Mail,
  ShieldCheck,
  XCircle,
} from 'lucide-react';

const StaffLoginAccess = ({
  formData,
  handleChange,
  roles = [],
  rolesLoading = false,
  onToggleRole,
  isEditMode = false,
  originalLoginEnabled = false,
  userId = null,
  userVerified = false,
}) => {
  const [isOpen, setIsOpen] = useState(true);

  const selectedRoleIds = Array.isArray(formData.selectedRoleIds)
    ? formData.selectedRoleIds.map(String)
    : [];

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className="flex w-full items-center justify-between border-b border-gray-100 px-6 py-4 text-left"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white">
            4
          </div>

          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Login Access
            </h2>
            <p className="text-xs text-gray-500">
              {isEditMode
                ? 'Enable, disable, or update staff login roles.'
                : 'Create a linked user and assign roles.'}
            </p>
          </div>
        </div>

        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-gray-500" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-500" />
        )}
      </button>

      {isOpen && (
        <div className="space-y-5 p-6">
          {isEditMode && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <p className="text-xs font-medium uppercase text-gray-500">
                  Current login
                </p>
                <div className="mt-2 flex items-center gap-2">
                  {originalLoginEnabled ? (
                    <>
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <span className="text-sm font-semibold text-green-700">
                        Enabled
                      </span>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-5 w-5 text-gray-500" />
                      <span className="text-sm font-semibold text-gray-600">
                        Disabled
                      </span>
                    </>
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <p className="text-xs font-medium uppercase text-gray-500">
                  Linked user
                </p>
                <p className="mt-2 text-sm font-semibold text-gray-700">
                  {userId ? 'Created' : 'Not created'}
                </p>
              </div>

              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <p className="text-xs font-medium uppercase text-gray-500">
                  Verification
                </p>
                <p className="mt-2 text-sm font-semibold text-gray-700">
                  {userVerified
                    ? 'Verified'
                    : userId
                      ? 'Pending'
                      : 'Not applicable'}
                </p>
              </div>
            </div>
          )}

          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 p-4 transition hover:bg-gray-50">
            <input
              type="checkbox"
              name="enableLogin"
              checked={Boolean(formData.enableLogin)}
              onChange={handleChange}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <div>
              <p className="font-semibold text-gray-900">
                {formData.enableLogin
                  ? 'Portal login enabled'
                  : 'Portal login disabled'}
              </p>
              <p className="mt-1 text-sm leading-6 text-gray-500">
                {formData.enableLogin
                  ? 'This staff member will be allowed to log in using their email address.'
                  : 'Select this option to create or reactivate login access for this staff member.'}
              </p>
            </div>
          </label>

          {formData.enableLogin && (
            <>
              <div className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
                <Mail className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
                <div className="text-sm text-blue-800">
                  <p className="font-semibold">Login email</p>
                  <p className="mt-1">
                    {formData.email || 'Enter a staff email address above.'}
                  </p>
                </div>
              </div>

              <div>
                <div className="mb-3 flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-gray-500" />
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">
                      Assign roles <span className="text-red-500">*</span>
                    </h3>
                    <p className="text-xs text-gray-500">
                      Select at least one role for this staff login.
                    </p>
                  </div>
                </div>

                {rolesLoading ? (
                  <div className="flex items-center justify-center rounded-xl border border-gray-200 py-8 text-sm text-gray-500">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading roles...
                  </div>
                ) : roles.length === 0 ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                    No active roles are available. Create a role from Settings → Roles & Permissions.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {roles.map((role) => {
                      const roleId = String(role.id);
                      const checked = selectedRoleIds.includes(roleId);

                      return (
                        <label
                          key={role.id}
                          className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition ${
                            checked
                              ? 'border-blue-300 bg-blue-50'
                              : 'border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => onToggleRole(role.id)}
                            className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div>
                            <p className="text-sm font-semibold text-gray-900">
                              {role.name}
                            </p>
                            <p className="mt-1 text-xs leading-5 text-gray-500">
                              {role.description || role.slug || 'School role'}
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex items-start gap-3 rounded-xl bg-gray-50 p-4 text-sm text-gray-600">
                <LockKeyhole className="mt-0.5 h-5 w-5 shrink-0" />
                <p className="leading-6">
                  When login is enabled for the first time, the system creates a linked user account. When a disabled login is enabled again, the existing account is reactivated.
                </p>
              </div>
            </>
          )}

          {isEditMode && originalLoginEnabled && !formData.enableLogin && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              Saving this form will disable this staff member’s login. Their staff profile and role assignments will remain available.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StaffLoginAccess;
